import { SsoAuthenticationSessionStorageKey } from '@/libs/rosa/lib/core/constants/authentication-session-storage-key.enum';
import { ReferenceListName } from '@/utils/reference-lists/reference-lists.interface';
import { ReferenceListsService } from '@/utils/reference-lists/reference-lists.service';
import { base64URLEncode } from '@/utils/toolkit/crypto/base64.utils';
import { sha256 } from '@/utils/toolkit/crypto/hash.utils';
import { randomCode } from '@/utils/toolkit/crypto/random-string.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { select, Store } from '@ngrx/store';
import { firstValueFrom, type Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import type { Branding } from '../../interfaces/Branding.interface';
import type { AppState } from '../../store/app.state';
import { BrandingStoreActions, BrandingStoreSelectors } from '../../store/branding';
import { StoreProvider } from '../store.provider';
import { AdvizeoBrandingProvider } from './advizeo-branding.provider';

const nonCustomBrandingKeys = ['advizeo', 'savee'];

@Injectable({
    providedIn: 'root'
})
export class BrandingProvider {
    private _store = inject<Store<AppState>>(Store);
    private _advizeoBrandingProvider = inject(AdvizeoBrandingProvider);
    private _referenceListsService = inject(ReferenceListsService);
    private _storeProvider = inject(StoreProvider);
    private _titleService = inject(Title);

    constructor() {
        this.getBranding$().subscribe(branding => {
            this._titleService.setTitle(branding.brandKey);

            let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                document.getElementsByTagName('head')[0].appendChild(link);
            }
            if (branding.faviconUrl) {
                link.href = branding.faviconUrl;
            } else if (nonCustomBrandingKeys.some(key => branding.brandKey.includes(key))) {
                link.href = 'assets/icons/savee_favicon.png';
            } else {
                link.href = 'assets/icons/default_favicon.ico';
            }
        });
    }

    getBranding$(): Observable<Branding> {
        return this._store.pipe(
            select(BrandingStoreSelectors.selectBranding),
            filter(isNotNullish),
            filter(brand => !!brand.brandKey)
        );
    }

    getBranding(): Branding | null {
        return this._storeProvider.getStoreInstantValue(BrandingStoreSelectors.selectBranding);
    }

    isNotCustomBranding$(): Observable<boolean> {
        return this._store.pipe(
            select(BrandingStoreSelectors.selectBranding),
            filter(isNotNullish),
            map(branding => branding.brandKey.includes('advizeo'))
        );
    }

    isCustomBranding$(): Observable<boolean> {
        return this._store.pipe(
            select(BrandingStoreSelectors.selectBranding),
            filter(isNotNullish),
            map(branding => !branding.brandKey.includes('advizeo'))
        );
    }

    isSsoAvailable$(): Observable<boolean> {
        return this._store.pipe(
            select(BrandingStoreSelectors.selectBranding),
            filter(isNotNullish),
            map(branding => this.isSsoAvailable(branding))
        );
    }

    isSsoMandatory$(): Observable<boolean> {
        return this._store.pipe(
            select(BrandingStoreSelectors.selectBranding),
            filter(isNotNullish),
            map(branding => isNotNullish(branding.idpLoginUrl) && branding.isSsoOnly)
        );
    }

    isSsoAvailable(branding: Branding): boolean {
        return isNotNullish(branding.idpLoginUrl);
    }

    shouldAutoLogin(branding: Branding): boolean {
        return isNotNullish(branding.idpLoginUrl) && isNotNullish(branding.idpLogoutUrl) && branding.isSsoOnly;
    }

    async connectWithSso() {
        const idpLoginUrl = this.getBranding()?.idpLoginUrl;
        const state = crypto.randomUUID();
        const codeVerifier = base64URLEncode(randomCode());
        const codeChallenge = base64URLEncode(await sha256(codeVerifier));

        sessionStorage.setItem(SsoAuthenticationSessionStorageKey.STATE_CLAIM, state);
        sessionStorage.setItem(SsoAuthenticationSessionStorageKey.CODE_VERIFIER, codeVerifier);

        if (isNotNullish(idpLoginUrl)) {
            window.location.href = idpLoginUrl + `&state=${state}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
        }
    }

    hasBrandingAccessToAdvizeoSupport$(): Observable<boolean> {
        return this._store.pipe(
            select(BrandingStoreSelectors.selectBranding),
            filter(isNotNullish),
            map(branding => branding.hasAccessToAdvizeoSupport)
        );
    }

    hasBrandingAccessToTutorial$(): Observable<boolean> {
        return this._store.pipe(
            select(BrandingStoreSelectors.selectBranding),
            filter(isNotNullish),
            map(branding => branding.hasAccessToTutorial)
        );
    }

    hasBrandingSimpleUserAccessToAdvizeoSupport$(): Observable<boolean> {
        return this._store.pipe(
            select(BrandingStoreSelectors.selectBranding),
            filter(isNotNullish),
            map(branding => branding.hasSimpleUserAccessToAdvizeoSupport)
        );
    }

    getSharedMessageFooter(): string {
        return this._referenceListsService.getReferenceListItemLabelR(
            ReferenceListName.BRANDING,
            `${this.getBranding()?.brandKey}.sharing`
        );
    }

    getPublicBrandingRootUri(): string {
        let branding = this.getBranding()?.rootUri;
        if (!branding) {
            throw new Error('Current branding is unknown');
        }
        if (branding === 'em.advizeo.app' || branding === 'em.advizeo.io') {
            // Todo delete second part when there is a redirection (SC-14062)
            branding = 'savee.advizeo.app';
        }
        return branding;
    }

    fetchBranding(rootUri: string): Promise<Branding> {
        this._store.dispatch(new BrandingStoreActions.FetchBranding());
        return firstValueFrom(this._advizeoBrandingProvider.getBranding$(rootUri))
            .then(branding => {
                this._store.dispatch(new BrandingStoreActions.FetchBrandingSucceeded({ branding }));
                return branding;
            })
            .catch(err => {
                console.error('failed fetching branding ::', err);
                this._store.dispatch(new BrandingStoreActions.FetchBrandingFailed());
                throw err;
            });
    }
}
